import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { AuthGroup } from 'root/src/server/util/awsClients/authApiClient.types';
import { useGroups } from 'root/src/client/v2/auth/hooks/useGroups';
import { useLocalStorage } from 'root/src/client/web/hooks/useLocalStorage';
import { getRedesignTwinPathname, getLegacyTwinPathname } from 'root/src/client/v2/layout/helpers/redesignAbRoutes';
// Where a non-cohort user is sent when they hit a redesign-only `/nova` page that has no legacy twin.
const NON_COHORT_FALLBACK_PATHNAME = '/';
// localStorage flag for internal testing: set `localStorage.setItem('dd-redesign-override', 'true')`
// in the devtools console (then reload) to force the redesign on for yourself without being in the
// `redesignBeta` auth group. `removeItem` reverts to the group-driven behavior. Client-only by design:
// localStorage is unreachable in the server pass, so an override load starts on the legacy page and
// this hook redirects after hydration (a brief legacy frame is expected, acceptable for internal use).
export const REDESIGN_OVERRIDE_STORAGE_KEY = 'dd-redesign-override';
// Redesign A/B test (client fallback + gate). The cohort is: auth group `redesignBeta` (assigned
// manually, lives in the JWT -> `groups` cookie, read via `useGroups()`) OR the localStorage override.
// Mounted once in `_app` so the whole rule lives in one place and no legacy page is edited.
//
// Two directions, both here:
//   - Cohort on a LEGACY page -> its redesign twin. The no-flash path for this is the server-side
//     redirect in `_app` getInitialProps (fires before any HTML on a hard load); this hook is the
//     client fallback (client-side navigation + a mid-session cohort change re-runs `useGroups`).
//   - NON-cohort on a `/nova` page -> back to legacy (block entry to the redesign). This stays
//     client-side only: the localStorage override is unreadable server-side, so gating here is the
//     only place that can honor it. A brief redesign frame is possible before the redirect on a hard
//     load (symmetric to the legacy-frame tradeoff above).
export const useRedesignAbRedirect = () => {
    const router = useRouter();
    const { groups } = useGroups();
    const [override] = useLocalStorage(REDESIGN_OVERRIDE_STORAGE_KEY, false);
    const isRedesignCohort = groups.includes(AuthGroup.RedesignBeta) || override === true;
    useEffect(() => {
        // Wait for the router to be ready, otherwise `router.query` is empty on dynamic routes and
        // the twin's `[recordId]`/`[id]` segment would not be filled.
        if (!router.isReady) {
            return;
        }
        if (isRedesignCohort) {
            // Cohort on a legacy page -> its redesign twin. The twin is not itself a forward-map key,
            // so this never loops. `replace` (not `push`) so Back does not bounce between the two.
            const twin = getRedesignTwinPathname(router.pathname);
            if (twin) {
                void router.replace({ pathname: twin, query: router.query });
            }
            return;
        }
        // Non-cohort must not enter `/nova`. Send them to the legacy twin (carrying dynamic params +
        // query) when one exists, otherwise home for redesign-only pages with no legacy equivalent.
        if (router.pathname.startsWith('/nova/')) {
            const legacy = getLegacyTwinPathname(router.pathname);
            void router.replace(legacy ? { pathname: legacy, query: router.query } : NON_COHORT_FALLBACK_PATHNAME);
        }
    }, [isRedesignCohort, router]);
};
